import React, { useState, useEffect } from 'react';
import fileDownload from 'js-file-download';
import DOMPurify from 'dompurify';
import * as _ from 'lodash';
import moment from 'moment';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import TextField from '@mui/material/TextField';
import { ReactComponent as DuplicateIcon } from '@Assets/duplicate.svg';
import { BsTicketDetailed as TicketIcon } from 'react-icons/bs';
import { ReactComponent as SyncedIcon } from '@Assets/synced.svg';
import { ReactComponent as UsersIcon } from '@Assets/users.svg';
import { ReactComponent as MailIcon } from '@Assets/mail.svg';
import { ReactComponent as ContactIcon } from '@Assets/phone.svg';
import {CopyToClipboard} from 'react-copy-to-clipboard';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import Select from '@mui/material/Select';
import FieldView from '@IssueConsoleRepresentationalDisplay/FieldView';
import UpdateReportersForm from '@IssueConsoleForms/UpdateReportersForm';
import SilentCloseConfirmForm from '@IssueConsoleForms/SilentCloseConfirmForm';
import CommentsTabs from '@IssueConsoleFunctionalDisplay/CommentsTabs';
import LabelClickUpMenu from '@AgileRepresentationalDisplay/LabelClickUpMenu';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DoneIcon from '@mui/icons-material/Done';
import { ReactComponent as RefreshIcon } from '@Assets/refresh.svg';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { ReactComponent as Edit } from '@Assets/editIcon.svg';
import { IoClose as Close } from 'react-icons/io5';
import RegistryLinkViewService from '@Helpers/URLHelpers/WeboutService/views/RegistryLinkViewService';
import RegistryLinkService from '@Helpers/URLHelpers/WeboutService/RegistryLinkService';
import LabelRegistryService from '@Helpers/URLHelpers/WeboutService/views/LabelRegistryService';
import ErrorHandler from '@Root/ErrorHandler';
import { useReduxFileStorage } from '@Hooks/reduxFileStorageHook/useReduxFileStorage';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useCursor } from '@Root/CursorContext';
import FieldService from '@Helpers/URLHelpers/FieldService';
import FieldEntryService from '@Helpers/URLHelpers/FieldEntryService';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import TicketService from '@Helpers/URLHelpers/KaiziqResolve/TicketService';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import './TicketView.scss';

// Matches the dashboard's own "stale" threshold (DashboardPage.js's STALE_DAYS) so a ticket
// flagged stale there is the same one that trips the silent-close prompt here.
const STALE_DAYS = 5;

export default function TicketView(props) {

    const [fieldList, setFieldList] = useState([]);
    const [viewLoader, setViewLoader] = useState(true);
    const [adminFieldList, setAdminFieldList] = useState([]);
    const { cursor, setCursor } = useCursor();
    const [ticket, setTicket] = useState({});
    const [status, setStatus] = useState();
    const [isPriorityEnable, setIsPriorityEnable] = useState(false);
    const [priority, setPriority] = useState();
    const [priorityList] = useState([
        {
            id: 0,
            title: 'Critical',
            value: 'critical',
            color: '#c31530',
        },
        {
            id: 1,
            title: 'High',
            value: 'high',
            color: '#807D7D',
        },
        {
            id: 2,
            title: 'Normal',
            value: 'normal',
            color: '#7ECA43',
        },
    ]);
    const [title, setTitle] = useState('');
    const [editTitle, setEditTitle] = useState(false);
    const [fieldEntries, setFieldEntries] = useState([]);
    const [slowInternetError] = useState(false);
    const [users, setUsers] = useState([{fullName: 'Unassigned', userName:'unassigned', id: 0}]);
    const [assigneeUser, setAssigneeUser] = useState({fullName: 'Unassigned', userName:'unassigned', id: 0});
    const [reporterUser, setReporterUser] = useState({registeredUser: false});
    const [editAssignee, setEditAssignee] = useState(false);
    const colorList = ['#2784B9', '#40c47d', '#FDB16C', '#b06060', '#f77474', '#6080b0', '#9a60b0', '#6cb060',
            '#b0ae60', '#65412b', '#cc9963', '#e1bd83', '#60748b', '#744700', '#c8d579', '#5f8fba', '#3e6073', '#8e6dc1', '#473785',
           '#9158a9',  '#7f3f7a', '#583e84', '#073763', '#20124d', '#3d124d', '#64113f', '#780448', '#003e23', '#108764', '#1a4c3d',
            '#c8d77f', '#11888b', '#1a95bd', '#0d6c7b', '#25b56e', '#006e3d'];
    const [labelListLoading, setLabelListLoading] = useState(false);
    const [, setInternetException] = useState(false);
    const [cloudSync, setCloudSync] = useState('done');
    const [snackBarOpen, setSnackBarOpen] = useState(false);
    const [snackBarType, setSnackBarType] = useState('success');
    const [snackBarMessage, setSnackBarMessage] = useState('');
    const [addLabelDropDown, setAddLabelDropDown] = useState(false);
    const [editReportersDetails, setEditReportersDetails] = useState(false);
    const [pdfDownloading, setPdfDownloading] = useState(false);
    // Holds the "resolved" status option while the silent-close prompt is open, so whichever
    // choice the agent makes can resume the same status change that triggered the prompt.
    const [showSilentCloseConfirm, setShowSilentCloseConfirm] = useState(false);
    const [pendingResolveStatusObj, setPendingResolveStatusObj] = useState(null);
    const labelRegistryDefaultFilters = {
        'labelRegistryFilter': {
            'projectIdList': [],
            'sortBy': 'createTimeStamp',
            'sortAsc': false
        },
        'page': 0,
        'size': 10
    }
    const [labelRegistryFilters, setLabelRegistryFilters] = useState(labelRegistryDefaultFilters);
    const [labelRegistryList, setLabelRegistryList] = useState([]);
    const [labelListOpen, setLabelListOpen] = useState(false);
    const [labelRegistryLinkList, setLabelRegistryLinkList] = useState([]);
    const [ currentUserProjectPermissions, setCurrentUserProjectPermissions ] = useState();
    let userSeatExpired = (!!localStorage.userSeatExpired && localStorage.userSeatExpired !== 'undefined')
        ? JSON.parse(localStorage.userSeatExpired) : false;
    const { fetchFileStorage } = useReduxFileStorage();
    const { activeUserId } = useReduxActiveUser();
    const { getUserPermissionByProjectId, getSimpleUsersByProjectId } = useReduxProject();
    const defaultFieldFilter = {
        projectIdList: [],
        tableType: 'kaiziqResolveConnection',
        tableId: 0,
        filterAttribute1List: []
    };
    const defaultFieldEntryFilter = {
        projectIdList: [],
        tableType: 'ticket',
        tableId: props.ticketId
    };
    const ticketService = new TicketService();
    const labelRegistryService = new LabelRegistryService();
    const registryLinkViewService = new RegistryLinkViewService();
    const registryLinkService = new RegistryLinkService();
    const userInfoService = new UserInfoService();
    const errorHandler = new ErrorHandler();
    const fieldService = new FieldService();
    const fieldEntryService = new FieldEntryService();
    const className = 'ticketView';

    useEffect(() => {
        renderData();
    }, [props.ticketId, props.open])

    const renderData = async () => {
        setViewLoader(true);
        let ticketObj = await renderTicketData();
        setStatus(ticketObj?.status);
        setPriority(ticketObj?.priority ?? 'normal');
        setIsPriorityEnable(props?.isPriorityEnable);
        setCurrentUserProjectPermissions(getUserPermissionByProjectId(ticketObj.projectId));
        await parseKaiziqResolveConnectionFields(ticketObj);
        await renderUserFilteredListData(ticketObj);
        await renderAssigneeAndReporterData(ticketObj);
        await renderRegistriesData(ticketObj);
        setViewLoader(false);
    }

    const renderTicketData = async() => {
        var loadLoops = 0, loadingError = false, errorException;
        let ticketObj;
        for(loadLoops = 0; loadLoops < 10; loadLoops++) {
            try {
                ticketObj = await ticketService.getTicket(props.ticketId);
                ticketObj = JSON.parse(ticketObj.body);
                setTicket(ticketObj);
                setTitle(ticketObj?.ticketTitle);
                await fetchFileStorage('TICKET', ticketObj.id);
                loadingError = false;
                break;
            } catch (err) {
                errorHandler.logError('Ticket Data', err);
                loadingError = true;
                errorException = err;
            }
        }
        if(loadLoops === 10 && loadingError === true) {
            errorHandler.handleError(errorException);
            setInternetException(true);
            return false;
        }
        return ticketObj;
    }

    const parseKaiziqResolveConnectionFields = async(ticketObj) => {
        var loadLoops = 0, loadingError = false, errorException;
        for(loadLoops = 0; loadLoops < 10; loadLoops++) {
            try {
                let formEntryField = _.cloneDeep(defaultFieldEntryFilter);
                formEntryField.projectIdList = [ticketObj?.projectId]
                let filter = {
                    fieldEntryFilter: formEntryField
                }
                let formEntryListObj = await fieldEntryService.getListFiltered(filter);
                formEntryListObj = JSON.parse(formEntryListObj.body);
                setFieldEntries(formEntryListObj?.result);
                let formFieldIdList = formEntryListObj?.result?.map(option => option.fieldId);
                let formFilter = _.cloneDeep(defaultFieldFilter);
                formFilter.projectIdList = [ticketObj?.projectId]
                formFilter.tableId = ticketObj?.kaiziqResolveConnectionId;
                formFilter.fieldIdList = formFieldIdList;
                filter = {
                    fieldFilter: formFilter
                }
                let formFieldListObj = await fieldService.getListFiltered(filter);
                formFieldListObj = JSON.parse(formFieldListObj.body);
                setFieldList(formFieldListObj?.result);
                let adminFilter = _.cloneDeep(defaultFieldFilter);
                adminFilter.filterAttribute1List = ['admin'];
                adminFilter.projectIdList = [ticketObj?.projectId]
                adminFilter.tableId = ticketObj?.kaiziqResolveConnectionId;
                filter = {
                    fieldFilter: adminFilter
                }
                let adminFieldListObj = await fieldService.getListFiltered(filter);
                adminFieldListObj = JSON.parse(adminFieldListObj.body);
                setAdminFieldList(adminFieldListObj?.result);
                loadingError = false;
                break;
            } catch (err) {
                errorHandler.logError('Ticket Fields Data', err);
                loadingError = true;
                errorException = err;
            }
        }
        if(loadLoops === 10 && loadingError === true) {
            errorHandler.handleError(errorException);
            setInternetException(true);
            return false;
        }
        return true;
    }

    const renderUserFilteredListData = async(ticketObj) => {
        var loadLoops = 0, loadingError = false, errorException;
        for(loadLoops = 0; loadLoops < 10; loadLoops++) {
            try {
                let allUsers = await getSimpleUsersByProjectId(ticketObj.projectId);
                let userFilteredList = allUsers?.filter((user) => user.id != ticketObj.assigneeUserId);
                setUsers(users.concat(userFilteredList));
                loadingError = false;
                break;
            } catch (err) {
                errorHandler.logError('User Filtered Data', err);
                loadingError = true;
                errorException = err;
            }
        }
        if(loadLoops === 10 && loadingError === true) {
            errorHandler.handleError(errorException);
            return false;
        }
        return true;
    }

    const renderAssigneeAndReporterData = async(ticketObj) => {
        var loadLoops = 0;
        let user;
        for(loadLoops = 0; loadLoops < 10; loadLoops++) {
            try {
                if(!!ticketObj?.assigneeUserId && ticketObj?.assigneeUserId !== 0 )
                {
                    user = await userInfoService.getUserById(ticketObj.assigneeUserId);
                    user = JSON.parse(user.body);
                    setAssigneeUser(user);
                }
                if(!!ticketObj?.createdByEmail && ticketObj?.createdByEmail.length !== 0 )
                {
                    user = await userInfoService.getUserByEmail(ticketObj?.createdByEmail);
                    user = JSON.parse(user.body);
                    setReporterUser(user);
                }
                break;
            } catch (err) {
                errorHandler.logError('Assignee Data', err);
            }
        }
        return true;
    }

    const renderRegistriesData = async(ticketObj) => {
        try {
            let labelRegistryFiltersObj = labelRegistryFilters;
            labelRegistryFiltersObj.labelRegistryFilter.projectIdList = [ticketObj.projectId];
            setLabelRegistryFilters(labelRegistryFiltersObj);
            await renderLabelRegistryList(ticketObj, labelRegistryFiltersObj);
            await renderLabelRegistryLinkList(ticketObj);
        } catch(err) {
            setInternetException(true);
        }
    }

    const renderLabelRegistryLinkList = async(ticketObj) => {
        var loadLoops = 0, loadingError = false, errorException;
        for(loadLoops = 0; loadLoops < 10; loadLoops++) {
            try {
                let labelRegistryLinkListObj = await registryLinkViewService.getListByTargetTypeAndTargetIdAndRegistryType('ticket', ticketObj.id, 'label');
                labelRegistryLinkListObj = JSON.parse(labelRegistryLinkListObj.body);
                setLabelRegistryLinkList(labelRegistryLinkListObj);
                loadingError = false;
                break;
            } catch (err) {
                errorHandler.logError('Label Registry Link List Data', err);
                loadingError = true;
                errorException = err;
            }
        }
        if(loadLoops === 10 && loadingError === true) {
            errorHandler.handleError(errorException);
            throw new Error(errorException);
        }
        return true;
    }

    const renderLabelRegistryList = async(ticketObj, filter) => {
        var loadLoops = 0, loadingError = false, errorException;
        for(loadLoops = 0; loadLoops < 10; loadLoops++) {
            try {
                let labelRegistryListObj = await labelRegistryService.getListFiltered(filter);
                labelRegistryListObj = JSON.parse(labelRegistryListObj.body);
                setLabelRegistryList(labelRegistryListObj?.result?.labelRegistryList);
                loadingError = false;
                break;
            } catch (err) {
                errorHandler.logError('Label Registry List Data', err);
                loadingError = true;
                errorException = err;
            }
        }
        if(loadLoops === 10 && loadingError === true) {
            errorHandler.handleError(errorException);
            throw new Error(errorException);
        }
        return true;
    }

    const handleLabelTextChange = async(e) => {
        setLabelListLoading(true);
        try {
            setLabelRegistryList([]);
            let labelSearchText = e.target.value;
            let labelRegistryFiltersObj = labelRegistryFilters;
            labelRegistryFiltersObj.labelRegistryFilter.projectIdList = [ticket.projectId];
            labelRegistryFiltersObj.labelRegistryFilter.searchQuery = labelSearchText;
            setLabelRegistryFilters(labelRegistryFiltersObj);
            await renderLabelRegistryList(ticket, labelRegistryFiltersObj);
        } catch (err) {
            errorHandler.logError('Label List Loading', err);
        }
        setLabelListLoading(false);
    }

    const handleLabelSelect = async(e, value) => {
        setCursor('wait');
        setCloudSync('syncing');
        try {
            let registryLinkObj = {
                projectId: ticket.projectId,
                registryType: value.type,
                registryId: value.id,
                targetType: 'ticket',
                targetId: ticket.id
            }
            registryLinkObj = await registryLinkService.createRegistryLink(registryLinkObj);
            JSON.parse(registryLinkObj.body);
            setCloudSync('done');
            setSnackBarMessage('Label has been successfully Added!');
            setSnackBarType('success');
            setSnackBarOpen(true);
            await renderLabelRegistryLinkList(ticket);
            await renderTicketData();
        } catch(err) {
            errorHandler.logError('Label Addition', err);
            setCloudSync('failed');
            setSnackBarMessage('Label Addition has Failed!');
            setSnackBarType('error');
            setSnackBarOpen(true);
        }
        setAddLabelDropDown(false);
        setCursor('auto');
    }

    const handleDeleteLabelRegistryClick = async(registryLinkId) => {
        setCursor('wait');
        setCloudSync('syncing');
        try {
            let registryLinkObj = {
                registryLinkId: registryLinkId
            }
            registryLinkObj = await registryLinkService.deleteRegistryLink(registryLinkId);
            JSON.parse(registryLinkObj.body);
            setCloudSync('done');
            setSnackBarMessage('Label has been successfully removed!');
            setSnackBarType('success');
            setSnackBarOpen(true);
            await renderTicketData();
            await renderLabelRegistryLinkList(ticket);
        } catch(err) {
            errorHandler.logError('Label Deletion', err);
            setCloudSync('failed');
            setSnackBarMessage('Label Deletion has Failed!');
            setSnackBarType('error');
            setSnackBarOpen(true);
        }
        setCursor('auto');
    }

    // A ticket nobody's touched in STALE_DAYS is exactly the case where resolving it normally
    // (email + WhatsApp to the reporter and agents) can land as an unwelcome surprise — see
    // performStatusUpdate's silent-close gating below.
    const getStaleDays = () => {
        const modifiedAt = Number(ticket?.modifiedTimeStamp);
        if (!modifiedAt) return 0;
        return Math.floor((Date.now() - modifiedAt) / (24 * 60 * 60 * 1000));
    };
    const isTicketStale = () => getStaleDays() >= STALE_DAYS;

    const performStatusUpdate = async(statusId, silent = false) => {
        setCursor('wait');
        setCloudSync('syncing');
        try {
            setStatus(statusId);
            let updateTicketMap = {
                id: props.ticketId,
                formId: ticket?.kaiziqResolveConnectionId,
                status: statusId,
                updatedByType: 'admin',
                updatedById: activeUserId,
                silent: !!silent
            }
            let updateTicketStatusRet;
            if(statusId === JSON.parse(adminFieldList?.find((field) => field.title === 'status')?.options)?.find((option) => option.value === 'escalated').id) {
                updateTicketStatusRet = await ticketService.escalateTicket(updateTicketMap);
                updateTicketStatusRet = JSON.parse(updateTicketStatusRet.body);
            } else {
                updateTicketStatusRet = await ticketService.updateTicketStatus(updateTicketMap);
                JSON.parse(updateTicketStatusRet.body);
            }
            setCloudSync('done');
            setSnackBarMessage(silent ? 'Ticket resolved silently — no notifications sent.' : 'Status has been successfully updated!');
            setSnackBarType('success');
            setSnackBarOpen(true);
            props.reloadData();
            await renderData();
        } catch (err) {
            errorHandler.handleError(err);
            setCloudSync('failed');
            setSnackBarMessage('Status update has failed');
            setSnackBarType('error');
            setSnackBarOpen(true);
        }
        setCursor('auto');
    };

    // Shared gate in front of performStatusUpdate: resolving a stale ticket pauses to ask
    // whether to skip the reporter/agent notifications instead of firing the update right away.
    const requestStatusUpdate = (statusId) => {
        const resolvedOption = JSON.parse(adminFieldList?.find((field) => field.title === 'status')?.options)
            ?.find((option) => option.value === 'resolved');
        if (resolvedOption && statusId === resolvedOption.id && isTicketStale()) {
            setPendingResolveStatusObj(resolvedOption);
            setShowSilentCloseConfirm(true);
            return;
        }
        performStatusUpdate(statusId);
    };

    const updateTicketStatus = (statusObj) => requestStatusUpdate(statusObj.id);

    const updateSelectTicketStatus = (e) => {
        e?.preventDefault();
        requestStatusUpdate(e.target.value);
    };

    const closeSilentCloseConfirm = () => {
        setShowSilentCloseConfirm(false);
        setPendingResolveStatusObj(null);
    };

    const handleCloseNormally = async() => {
        const statusObj = pendingResolveStatusObj;
        closeSilentCloseConfirm();
        if (statusObj) await performStatusUpdate(statusObj.id, false);
    };

    const handleSilentClose = async() => {
        const statusObj = pendingResolveStatusObj;
        closeSilentCloseConfirm();
        if (statusObj) await performStatusUpdate(statusObj.id, true);
    };

    const updateSelectTicketPriority = async(e) => {
        e?.preventDefault();
        setCursor('wait');
        setCloudSync('syncing');
        try {
            let priority = e.target.value;
            setPriority(priority);
            let updateTicketMap = {
                id: props.ticketId,
                formId: ticket?.kaiziqResolveConnectionId,
                priority: priority,
                updatedByType: 'admin',
                updatedById: activeUserId
            }
            let updateTicketPriorityRet = await ticketService.updateTicketPriority(updateTicketMap);
            JSON.parse(updateTicketPriorityRet.body);
            setCloudSync('done');
            setSnackBarMessage('Priority has been successfully updated!');
            setSnackBarType('success');
            setSnackBarOpen(true);
            props.reloadData();
            await renderData();
        } catch (err) {
            errorHandler.handleError(err);
            setCloudSync('failed');
            setSnackBarMessage('Priority update has failed');
            setSnackBarType('error');
            setSnackBarOpen(true);
        }
        setCursor('auto');
    };

    const handleSnackBarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackBarOpen(false);
    };

    const toggleAddLabelDropDown = (e) => {
        setAddLabelDropDown(!addLabelDropDown);
    };

    const getInitials = (string) => {
        if(!!string && string.length > 0) {
            var names = string?.split(' '),
                initials = names[0]?.substring(0, 1)?.toUpperCase();

            if (names?.length > 1) {
                initials += names[names?.length - 1]?.substring(0, 1)?.toUpperCase();
            }
            return initials;
        }
        return 'N/A';
    };

    const assignToSelf = async() => {
        console.log(users);
        try {
            if(ticket?.assigneeUserId === activeUserId || ticket?.assigneeUserId === JSON.parse(activeUserId)){
                setCloudSync('done');
                setSnackBarMessage('This Ticket is already assigned to you!');
                setSnackBarType('info');
                setSnackBarOpen(true);
                return;
            }
        } catch(err) {
        }
        if(users.indexOf(users.find((user) => user.id === JSON.parse(activeUserId))) > -1 ){
            setCursor('wait');
            setCloudSync('syncing');
            try {
                let assigneeUserObj = users.find((user) => user.id === JSON.parse(activeUserId));
                setAssigneeUser(assigneeUserObj);
                let user = await userInfoService.getUserById(assigneeUserObj.id);
                user = JSON.parse(user.body);
                setAssigneeUser(user);
                let updateTicketMap = {
                    id: props.ticketId,
                    assigneeUserId: user.id,
                    updatedByType: 'admin',
                    updatedById: activeUserId
                }
                let ticketObj = await ticketService.updateTicketAssignee(updateTicketMap);
                ticketObj = JSON.parse(ticketObj.body);
                setCloudSync('done');
                setSnackBarMessage('Assignee has been successfully changed to you!');
                setSnackBarType('success');
                setSnackBarOpen(true);
                await renderAssigneeAndReporterData(ticketObj);
                props.reloadData();
            } catch(err) {
                errorHandler.logError('Assign To Self', err);
                setCloudSync('failed');
                setSnackBarMessage('Assignee change failed!');
                setSnackBarType('error');
                setSnackBarOpen(true);
            }
            setCursor('auto');
        }
    }

    const handleAssigneeChange = async(e, value) => {
        setCursor('wait');
        setCloudSync('syncing');
        try {
            setAssigneeUser(value);
            let user = await userInfoService.getUserById(value.id);
            setAssigneeUser(JSON.parse(user.body));
            let updateTicketMap = {
                id: props.ticketId,
                assigneeUserId: value.id,
                updatedByType: 'admin',
                updatedById: activeUserId
            }
            let ticketObj = await ticketService.updateTicketAssignee(updateTicketMap);
            ticketObj = JSON.parse(ticketObj.body);
            setTicket(ticketObj);
            setCloudSync('done');
            setSnackBarMessage('Assignee has been successfully changed!');
            setSnackBarType('success');
            setSnackBarOpen(true);
            await renderAssigneeAndReporterData(ticketObj);
            props.reloadData();
        } catch(err) {
            errorHandler.logError('Assignee Change', err);
            setCloudSync('failed');
            setSnackBarMessage('Assignee change failed!');
            setSnackBarType('error');
            setSnackBarOpen(true);
        }
        setCursor('auto');
        setEditAssignee(false);
    }

    const dataURLtoFile = (dataurl) => {
        var arr = dataurl.split(','),
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], { type: mime });
    }

    const handleCopyAlert = () => {
        setSnackBarMessage('Copied to Clipboard');
        setSnackBarType('success');
        setSnackBarOpen(true);
    }

    const handleTitleChange = (e) => {
        let sanitizedTitle = DOMPurify.sanitize(e.target.value);
        setTitle(sanitizedTitle);
    }

    const editTitleOnKeyPress = (e) => {
        if(e.keyCode === 13) {
            toggleEditTitle()
        }
    }

    const toggleEditTitle = () => {
        if(editTitle) {
            handleTitleSubmit();
        }
        setEditTitle(!editTitle);
    }

    const handleTitleSubmit = async()=> {
        setCursor('wait');
        setCloudSync('syncing');
        try {
            setTitle(title);
            let updateTicket = ticket;
            updateTicket.ticketTitle = title;
            let ticketObj = await ticketService.updateTicket(updateTicket);
            ticketObj = JSON.parse(ticketObj.body);
            setTicket(ticketObj);
            setCloudSync('done');
            setSnackBarMessage('Ticket Title has been successfully changed!');
            setSnackBarType('success');
            setSnackBarOpen(true);
        } catch(err) {
            errorHandler.logError('Ticket Title Change', err);
            setCloudSync('failed');
            setSnackBarMessage('Ticket Title change failed!');
            setSnackBarType('error');
            setSnackBarOpen(true);
        }
        setCursor('auto');
        setEditAssignee(false);
    }

    const toggleEditReportersDetails = ()=> {
        setEditReportersDetails(!editReportersDetails);
    }

    const handleDownloadPdf = async () => {
        if (pdfDownloading) return;
        setPdfDownloading(true);
        try {
            const pdfResponse = await ticketService.getTicketAsPdf('agent', props.ticketId);

            let parsedResponse = pdfResponse;
            if (typeof parsedResponse === 'string') {
                try {
                    parsedResponse = JSON.parse(parsedResponse);
                } catch (e) {
                    // Keep raw value for backward compatibility where API returns base64 directly.
                }
            }

            let responseBody = parsedResponse?.body;
            if (typeof responseBody === 'string') {
                try {
                    responseBody = JSON.parse(responseBody);
                } catch (e) {
                    // Ignore and continue with fallback extraction.
                }
            }

            let fileClob = responseBody?.fileClob ?? parsedResponse?.fileClob;
            if (typeof fileClob === 'string') {
                try {
                    fileClob = JSON.parse(fileClob);
                } catch (e) {
                    // Ignore and continue with fallback extraction.
                }
            }

            let fileBase64 =
                fileClob?.file ??
                responseBody?.file ??
                parsedResponse?.file ??
                (typeof pdfResponse === 'string' ? pdfResponse : null);

            if (!fileBase64 || typeof fileBase64 !== 'string') {
                throw new Error('No PDF data received');
            }

            fileBase64 = fileBase64.replace(/^data:application\/pdf;base64,/, '');
            const bytes = atob(fileBase64);
            const byteArray = new Uint8Array(bytes.length);
            for (let i = 0; i < bytes.length; i++) byteArray[i] = bytes.charCodeAt(i);
            const fileType = fileClob?.fileType || 'application/pdf';
            const fileName = fileClob?.fileName || `ticket-${ticket.ticketId || props.ticketId}.pdf`;
            const blob = new Blob([byteArray], { type: fileType });
            fileDownload(blob, fileName, fileType);
        } catch (err) {
            errorHandler.logError('PDF Download', err);
            setSnackBarMessage('Failed to download PDF');
            setSnackBarType('error');
            setSnackBarOpen(true);
        }
        setPdfDownloading(false);
    }

    const handleCopyLink = () => {
        const formId = props.formId;
        const ticketId = props.ticketId;
        const url = `${window.location.origin}/#/forms?formId=${formId}&ticketId=${ticketId}`;
        navigator.clipboard.writeText(url).then(() => {
            setSnackBarMessage('Link copied to clipboard');
            setSnackBarType('success');
            setSnackBarOpen(true);
        });
    };

    return (
        <Drawer
            onClose={props.closePopup}
            size={'lg'}
            anchor='right'
            open={props.open}
            style={{ cursor: cursor }}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
                sx: {
                    width: { xs: '100%', md: 1080 },
                    maxWidth: '100vw',
                    borderTopLeftRadius: 16,
                    borderBottomLeftRadius: 16,
                    boxShadow: '0 16px 32px rgba(0,0,0,0.18)',
                    backgroundColor: 'var(--color-componentBackground)',
                    color: 'var(--color-text)',
                }
            }}
        >
            <div className={className}>
                <div className={className + '__inner height-wfa flex-column-flex-start'}>
                    {
                        viewLoader ?
                            <Box className={className + '__skeleton'}>
                                {/* Header Skeleton */}
                                <Box className={className + '__skeleton__header'}>
                                    <Box className={className + '__skeleton__headerLeft'}>
                                        <Skeleton variant="circular" width={24} height={24} />
                                        <Skeleton variant="text" width={120} height={28} />
                                    </Box>
                                    <Box className={className + '__skeleton__headerRight'}>
                                        <Skeleton variant="text" width={140} height={24} />
                                        <Skeleton variant="text" width={80} height={24} />
                                        <Skeleton variant="circular" width={32} height={32} />
                                    </Box>
                                </Box>

                                {/* Body Skeleton */}
                                <Box className={className + '__skeleton__body'}>
                                    <Box className={className + '__skeleton__left'}>
                                        {/* Title Card */}
                                        <Box className={className + '__skeleton__card'}>
                                            <Box className={className + '__skeleton__cardHeader'}>
                                                <Skeleton variant="text" width={60} height={24} />
                                                <Skeleton variant="circular" width={20} height={20} />
                                            </Box>
                                            <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: '8px' }} />
                                        </Box>

                                        {/* People Card */}
                                        <Box className={className + '__skeleton__card'}>
                                            <Box className={className + '__skeleton__row'}>
                                                <Box className={className + '__skeleton__col'}>
                                                    <Skeleton variant="text" width={80} height={24} />
                                                    <Box className={className + '__skeleton__userRow'}>
                                                        <Skeleton variant="circular" width={28} height={28} />
                                                        <Skeleton variant="text" width={120} height={20} />
                                                    </Box>
                                                </Box>
                                                <Box className={className + '__skeleton__col'}>
                                                    <Box className={className + '__skeleton__assigneeHeader'}>
                                                        <Skeleton variant="text" width={80} height={24} />
                                                        <Skeleton variant="text" width={90} height={20} />
                                                    </Box>
                                                    <Box className={className + '__skeleton__userRow'}>
                                                        <Skeleton variant="circular" width={28} height={28} />
                                                        <Skeleton variant="text" width={120} height={20} />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>

                                        {/* Status/Priority Card */}
                                        <Box className={className + '__skeleton__card'}>
                                            <Box className={className + '__skeleton__row'}>
                                                {isPriorityEnable && (
                                                    <Box className={className + '__skeleton__col'}>
                                                        <Skeleton variant="text" width={60} height={24} />
                                                        <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: '8px' }} />
                                                    </Box>
                                                )}
                                                <Box className={className + '__skeleton__col'}>
                                                    <Box className={className + '__skeleton__statusHeader'}>
                                                        <Skeleton variant="text" width={60} height={24} />
                                                        <Skeleton variant="text" width={120} height={20} />
                                                    </Box>
                                                    <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: '8px' }} />
                                                </Box>
                                            </Box>
                                        </Box>

                                        {/* Labels Card */}
                                        <Box className={className + '__skeleton__card'}>
                                            <Box className={className + '__skeleton__labelHeader'}>
                                                <Skeleton variant="text" width={80} height={24} />
                                                <Skeleton variant="text" width={90} height={20} />
                                            </Box>
                                            <Box className={className + '__skeleton__chips'}>
                                                <Skeleton variant="rectangular" width={90} height={28} sx={{ borderRadius: '14px' }} />
                                                <Skeleton variant="rectangular" width={110} height={28} sx={{ borderRadius: '14px' }} />
                                                <Skeleton variant="rectangular" width={80} height={28} sx={{ borderRadius: '14px' }} />
                                            </Box>
                                        </Box>

                                        {/* Custom Fields Card */}
                                        <Box className={className + '__skeleton__card'}>
                                            {[...Array(4)].map((_, i) => (
                                                <Box key={i} className={className + '__skeleton__field'}>
                                                    <Skeleton variant="text" width={100} height={20} />
                                                    <Skeleton variant="text" width="80%" height={24} />
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>

                                    {/* Right Column Skeleton */}
                                    <Box className={className + '__skeleton__right'}>
                                        <Box className={className + '__skeleton__comments'}>
                                            <Box className={className + '__skeleton__tabs'}>
                                                <Skeleton variant="rectangular" width="50%" height={56} />
                                                <Skeleton variant="rectangular" width="50%" height={56} />
                                            </Box>
                                            <Box className={className + '__skeleton__commentList'}>
                                                {[...Array(5)].map((_, i) => (
                                                    <Box key={i} className={className + '__skeleton__comment'}>
                                                        <Skeleton variant="circular" width={36} height={36} />
                                                        <Box className={className + '__skeleton__commentContent'}>
                                                            <Skeleton variant="text" width={120} height={20} />
                                                            <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: '12px' }} />
                                                            <Skeleton variant="text" width={80} height={16} />
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                            <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: '12px' }} />
                                        </Box>
                                    </Box>
                                </Box>

                                {slowInternetError && (
                                    <Box className={className + '__skeleton__hint'}>
                                        <Typography className={className + '__skeleton__hintText'}>
                                            This is taking a while to load...
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        :
                            <>
                                {/* Sticky header bar */}
                                <div className={className + '__inner__titleBar width-wfa ticketView__header'}>
                                    <div className={className + '__inner__titleBar__left flex-row-center-0p5rem-gap semi-large-text'}>
                                        <TicketIcon className={className + '__inner__titleBar__left__icon'}/>
                                        <div className={className + '__inner__titleBar__t1'}>
                                            {ticket.ticketId}
                                        </div>
                                    </div>
                                    <div className={className + '__inner__titleBar__right flex-row-center-1rem-gap height-wfa'}>
                                        <div className={className + '__inner__titleBar__right__create flex-row-center-0p5rem-gap grey-text'}>
                                            Reported: {moment(new Date(ticket?.createTimeStamp),'DD-MM-YYYY').fromNow()}
                                        </div>
                                        {
                                            !!adminFieldList?.find((field) => field.title === 'status')?.options &&
                                                <button
                                                    type='button'
                                                    className={className + '__inner__titleBar__right__escalate flex-row-center-0p5rem-gap'}
                                                    onClick={() => updateTicketStatus(JSON.parse(adminFieldList?.find((field) => field.title === 'status')?.options)?.find((option) => option.value === 'escalated'))}>
                                                    <WarningAmberRoundedIcon/>
                                                    <span className={className + '__inner__titleBar__right__escalate__t1 no-wrap'}>
                                                        Escalate
                                                    </span>
                                                </button>
                                        }
                                        {
                                            cloudSync === 'syncing' &&
                                                <div className={className + '__inner__titleBar__sync'}>
                                                    <CloudSyncIcon className={className + '__inner__titleBar__syncIcon--blue'}/>
                                                    <span className={className + '__inner__titleBar__syncLabel'}>Syncing</span>
                                                </div>
                                        }
                                        {
                                            cloudSync === 'done' &&
                                                <div className={className + '__inner__titleBar__sync'}>
                                                    <SyncedIcon className={className + '__inner__titleBar__syncIcon--blue'}/>
                                                </div>
                                        }
                                        {
                                            cloudSync === 'failed' &&
                                                <div className={className + '__inner__titleBar__sync'}>
                                                    <ErrorOutlineRoundedIcon className={className + '__inner__titleBar__syncIcon--red'}/>
                                                    <span className={className + '__inner__titleBar__syncLabel no-wrap'}>Sync failed</span>
                                                    <RefreshIcon className={className + '__inner__titleBar__syncRefresh'} onClick={renderData}/>
                                                </div>
                                        }
                                        <Tooltip title="Copy link" placement="bottom">
                                            <button
                                                type="button"
                                                className={className + '__inner__titleBar__right__download'}
                                                onClick={handleCopyLink}
                                            >
                                                <LinkRoundedIcon fontSize="small" />
                                            </button>
                                        </Tooltip>
                                        <Tooltip title="Download as PDF" placement="bottom">
                                            <button
                                                type="button"
                                                className={className + '__inner__titleBar__right__download'}
                                                onClick={handleDownloadPdf}
                                                disabled={pdfDownloading}
                                            >
                                                <DownloadRoundedIcon fontSize="small" />
                                            </button>
                                        </Tooltip>
                                        <Close className={className + '__inner__titleBar__close close-button'}
                                            onClick={props.closePopup}/>
                                    </div>
                                </div>
                                {cloudSync === 'syncing' && (
                                    <LinearProgress className={className + '__progressTop'} />
                                )}

                                {/* Content */}
                                <div className={className + '__inner__body flex-row-flex-start width-wfa ticketView__body ' + (userSeatExpired ? ' disabled' : '')}>
                                    <div className={className + '__inner__body__left flex-column-flex-start gap-1rem ticketView__left height-wfa'}>

                                        {/* Title */}
                                        <div className={className + '__card'}>
                                            <div className={className + '__inner__body__left__title flex-column-flex-start gap-1rem width-wfa'}>
                                                <div className={className + '__inner__body__left__title__t1 flex-row-center gap-1rem semi-bold-text'}>
                                                    Title*
                                                    <Edit className={className + '__inner__body__left__title__value__text__icon dashboard-icon'} onClick={toggleEditTitle}/>
                                                </div>
                                                <div className={className + '__inner__body__left__title__value width-wfa'}>
                                                    {
                                                        editTitle ?
                                                            <div className={className + '__inner__body__left__title__value__flex'} onBlur={toggleEditTitle}>
                                                                <TextField
                                                                    placeholder='Enter title ...'
                                                                    className={className + '__inner__body__left__title__value__flex__textField width-wfa'}
                                                                    type='text'
                                                                    helperText='Press Enter to Save'
                                                                    disabled={!currentUserProjectPermissions?.allowCreate || cursor === 'wait'}
                                                                    onChange={handleTitleChange}
                                                                    onKeyDown={editTitleOnKeyPress}
                                                                    inputProps={{ maxLength: 200 }}
                                                                    value={title}
                                                                    maxRows='1'
                                                                    variant='outlined'
                                                                    required/>
                                                            </div>
                                                        :
                                                            <div className={className + '__inner__body__left__title__value__text'}>
                                                                {title ?? 'N/A'}
                                                            </div>
                                                    }
                                                </div>
                                            </div>
                                        </div>

                                        {/* People */}
                                        <div className={className + '__card'}>
                                            <div className={className + '__inner__body__left__users flex-row-flex-start width-wfa'}>
                                                <div className={className + '__inner__body__left__users__reporter flex-column-flex-start gap-1rem width-wfa'}>
                                                    <div className={className + '__inner__body__left__users__reporter__title flex-row-center gap-1rem width-wfa'}>
                                                        <div className={className + '__inner__body__left__users__reporter__title__t1 semi-bold-text'}>
                                                            Reporter
                                                        </div>
                                                        {
                                                            currentUserProjectPermissions?.allowEdit ?
                                                                <Edit
                                                                    className='dashboard-icon'
                                                                    onClick={toggleEditReportersDetails}/>
                                                            :
                                                                null
                                                        }
                                                    </div>
                                                    {
                                                        !reporterUser?.fullName ?
                                                            <div className={className + '__inner__body__left__users__reporter__details flex-column-flex-start gap-1rem'}>
                                                                <div className={className + '__inner__body__left__users__reporter__details__name flex-row-center-0p5rem-gap'}>
                                                                    <UsersIcon className={className + '__inner__body__left__users__reporter__details__name__icon standard-icon'}/>
                                                                    <div className={className + '__inner__body__left__users__reporter__details__name__t1 flex-row-center-1rem-gap blue-text '}>
                                                                        {ticket?.createdByFullName}
                                                                        <CopyToClipboard
                                                                            text={ticket?.createdByFullName}
                                                                            onCopy={handleCopyAlert}>
                                                                            <DuplicateIcon className={className + '__inner__body__left__users__reporter__details__name__t1__icon small-dashboard-icon'}/>
                                                                        </CopyToClipboard>
                                                                    </div>
                                                                </div>
                                                                <div className={className + '__inner__body__left__users__reporter__details__name flex-row-center-0p5rem-gap'}>
                                                                    <MailIcon className={className + '__inner__body__left__users__reporter__details__name__icon standard-icon'}/>
                                                                    <div className={className + '__inner__body__left__users__reporter__details__name__t1 flex-row-center-1rem-gap blue-text '}>
                                                                        {ticket?.createdByEmail}
                                                                        <CopyToClipboard
                                                                            text={ticket?.createdByEmail}
                                                                            onCopy={handleCopyAlert}>
                                                                            <DuplicateIcon className={className + '__inner__body__left__users__reporter__details__name__t1__icon small-dashboard-icon'}/>
                                                                        </CopyToClipboard>
                                                                    </div>
                                                                </div>
                                                                {
                                                                    !!ticket?.createdByContactNumber &&
                                                                        <div className={className + '__inner__body__left__users__reporter__details__name flex-row-center-0p5rem-gap'}>
                                                                            <ContactIcon className={className + '__inner__body__left__users__reporter__details__name__icon standard-icon'}/>
                                                                            <div className={className + '__inner__body__left__users__reporter__details__name__t1 flex-row-center-1rem-gap blue-text '}>
                                                                                {' +' + ticket?.createdByContactNumber}
                                                                                <CopyToClipboard
                                                                                    text={ticket?.createdByContactNumber}
                                                                                    onCopy={handleCopyAlert}>
                                                                                    <DuplicateIcon className={className + '__inner__body__left__users__reporter__details__name__t1__icon small-dashboard-icon'}/>
                                                                                </CopyToClipboard>
                                                                            </div>
                                                                        </div>
                                                                }
                                                            </div>
                                                        :
                                                            <div className={className + '__inner__body__left__users__reporter__name flex-row-center-0p5rem-gap'}>
                                                                {!!reporterUser.photo && reporterUser.photo !== undefined && reporterUser.photo !== ''?
                                                                        <Avatar src={URL.createObjectURL(dataURLtoFile(reporterUser.photo))} sx={{ width: '25px', height: '25px' }} />
                                                                    :
                                                                        <Avatar sx={{ bgcolor: colorList[reporterUser.fullName.charCodeAt(0) % colorList.length] + ' !important', width: '20px', height: '20px', fontSize: '10px' }}>{getInitials(reporterUser.fullName)}</Avatar>
                                                                }
                                                                {
                                                                    reporterUser?.fullName?.split(' ')[0] + ' ' +
                                                                    (function () {
                                                                        const parts = reporterUser?.fullName?.split(' ');
                                                                        return parts?.length > 1 ? parts[parts.length - 1][0] + '.' : '';
                                                                    })()
                                                                }
                                                            </div>
                                                    }
                                                </div>
                                                <div className={className + '__inner__body__left__users__assignee flex-column-flex-start gap-1rem width-wfa'} >
                                                    <div className={className + '__inner__body__left__users__assignee__title flex-row-center-space-between width-wfa'}>
                                                        <div className={className + '__inner__body__left__users__assignee__title__left flex-row-center gap-1rem'}>
                                                            <div className={className + '__inner__body__left__users__assignee__title__left__t1 semi-bold-text'}>
                                                                Assignee
                                                            </div>
                                                            <Edit
                                                                className='dashboard-icon'
                                                                onClick={() => setEditAssignee(true)}/>
                                                        </div>
                                                        <div className={className + '__inner__body__left__users__assignee__title__button flex-row-center standard-link'} onClick={assignToSelf}>
                                                            Assign To Me
                                                        </div>
                                                    </div>
                                                    {
                                                        editAssignee ?
                                                            <Autocomplete
                                                              options={users}
                                                              value={assigneeUser}
                                                              className={className + '__inner__body__left__users__assignee__select width-wfa'}
                                                              disabled={!currentUserProjectPermissions?.allowCreate || cursor === 'wait'}
                                                              getOptionLabel={(option) => option?.fullName + ' : ' + option?.userName}
                                                              isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                                              onChange={handleAssigneeChange}
                                                              renderInput={(params) => <TextField {...params} placeholder='Unassigned' variant='outlined' required/>}/>
                                                        :
                                                            <div className={className + '__inner__body__left__form__assignee__name flex-row-center-0p5rem-gap'}>
                                                                {!!assigneeUser?.photo && assigneeUser.photo !== undefined && assigneeUser.photo !== '' ?
                                                                    <Avatar src={URL.createObjectURL(dataURLtoFile(assigneeUser.photo))} sx={{ width: '25px', height: '25px' }} />
                                                                :
                                                                    <Avatar sx={{ bgcolor: colorList[assigneeUser?.fullName?.charCodeAt(0) % colorList.length] + ' !important', width: '20px', height: '20px', fontSize: '10px' }}>{getInitials(assigneeUser?.fullName)}</Avatar>
                                                                }
                                                                {
                                                                    assigneeUser?.fullName?.split(' ')[0] + ' ' +
                                                                    (() => {
                                                                        const parts = assigneeUser?.fullName?.split(' ');
                                                                        return parts?.length > 1 ? parts[parts.length - 1][0] + '.' : '';
                                                                    })()
                                                                }
                                                            </div>

                                                    }
                                                </div>
                                            </div>
                                        </div>

                                        {/* Status / Priority */}
                                        <div className={className + '__card'}>
                                            <div className={className + '__inner__body__left__level flex-row-flex-start-space-between width-wfa'}>
                                                {
                                                    isPriorityEnable &&
                                                        <div className={className + '__inner__body__left__level__priority flex-column-flex-start-1vh-gap'}>
                                                            <div className={className + '__inner__body__left__level__priority__title semi-bold-text flex-row-center-space-between width-wfa'}>
                                                                <div className={className + '__inner__body__left__level__priority__title__t1'}>
                                                                    Priority
                                                                </div>
                                                            </div>
                                                            <Select className={className + '__inner__body__left__level__priority__select standard-status-select width-wfa'}
                                                                variant='outlined' defaultValue={priority} onChange={updateSelectTicketPriority}
                                                                disabled={!currentUserProjectPermissions?.allowCreate}>
                                                                {
                                                                    priorityList?.map((option)=>
                                                                        <MenuItem value={option?.value} className={className + '__inner__body__left__level__priority__select__option '}>
                                                                            <div className={className + '__inner__body__left__level__priority__select__option flex-row-center-1vw-gap'}>
                                                                                <div className={className + '__inner__body__left__level__priority__select__option__indicator indicator-box'}
                                                                                    style={{background: option?.color}}/>
                                                                                <div className={className + '__inner__body__left__level__priority__select__option__value'}>
                                                                                    {option?.title}
                                                                                </div>
                                                                            </div>
                                                                        </MenuItem>
                                                                    )
                                                                }
                                                            </Select>
                                                        </div>
                                                }
                                                <div className={className + '__inner__body__left__level__status flex-column-flex-start-1vh-gap'}>
                                                    <div className={className + '__inner__body__left__level__status__title semi-bold-text flex-row-center-space-between width-wfa'}>
                                                        <div className={className + '__inner__body__left__level__status__title__t1'}>
                                                            Status
                                                        </div>
                                                        {
                                                            !!adminFieldList?.find((field) => field.title === 'status')?.options &&
                                                                (
                                                                    JSON.parse(adminFieldList?.find((field) => field.title === 'status')?.options)?.find((option) => option.id === Number(ticket?.status))?.value !== 'resolved' ?
                                                                        <div className={className + '__inner__body__left__level__status__title__button flex-row-center-0p5rem-gap standard-link'}
                                                                            onClick={(e) => updateTicketStatus(JSON.parse(adminFieldList?.find((field) => field.title === 'status')?.options)?.find((option) => option.value === 'resolved'))}>
                                                                            <div className={className + '__inner__body__left__level__status__title__button__t1 no-wrap '}>
                                                                                Mark as Resolved
                                                                            </div>
                                                                        </div>
                                                                    :
                                                                        <div className={className + '__inner__body__left__level__status__title__button flex-row-center-0p5rem-gap standard-link'}
                                                                            onClick={(e) => updateTicketStatus(JSON.parse(adminFieldList?.find((field) => field.title === 'status')?.options)?.find((option) => option.value === 'reopened'))}>
                                                                            <div className={className + '__inner__body__left__level__status__title__button__t1 no-wrap '}>
                                                                                Re-Open
                                                                            </div>
                                                                        </div>
                                                                )
                                                        }
                                                    </div>
                                                    <Select className={className + '__inner__body__left__level__status__select standard-status-select width-wfa'}
                                                        variant='outlined' defaultValue={status} onChange={updateSelectTicketStatus}
                                                        disabled={!currentUserProjectPermissions?.allowCreate}>
                                                        {
                                                            !!adminFieldList?.find((field) => field.title === 'status') &&
                                                                JSON.parse(adminFieldList?.find((field) => field.title === 'status')?.options)?.map((option) =>
                                                                <MenuItem value={option?.id} className={className + '__inner__body__left__level__status__select__option '}>
                                                                    <div className={className + '__inner__body__left__level__status__select__option flex-row-center-1vw-gap'}>
                                                                        <div className={className + '__inner__body__left__level__status__select__option__indicator indicator-box'}
                                                                            style={{background: option?.color}}/>
                                                                        <div className={className + '__inner__body__left__level__status__select__option__value'}>
                                                                            {option?.title}
                                                                        </div>
                                                                    </div>
                                                                </MenuItem>
                                                            )
                                                        }
                                                    </Select>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Labels */}
                                        <div className={className + '__card'}>
                                            <div className={className + '__inner__body__left__labels flex-column-flex-start gap-1rem width-wfa'}>
                                                <div className={className + '__inner__body__left__labels__title semi-bold-text flex-row-center-space-between width-wfa'}>
                                                    <div className={className + '__inner__body__left__labels__title__t1'}>
                                                        Labels
                                                    </div>
                                                    {
                                                        addLabelDropDown ?
                                                            <Button variant='text'>
                                                                <div className={className + '__inner__body__left__labels__title__button flex-row-center-1vw-gap'}
                                                                    onClick={toggleAddLabelDropDown}>
                                                                    <DoneIcon/>
                                                                    <div className={className + '__inner__body__left__labels__title__button__t1 no-wrap '}>
                                                                        Done
                                                                    </div>
                                                                </div>
                                                            </Button>
                                                        :
                                                            <Button variant='text'>
                                                                <div className={className + '__inner__body__left__labels__title__button flex-row-center-1vw-gap'}
                                                                    onClick={toggleAddLabelDropDown}>
                                                                    <AddCircleOutlineIcon/>
                                                                    <div className={className + '__inner__body__left__labels__title__button__t1 no-wrap '}>
                                                                        Add Label
                                                                    </div>
                                                                </div>
                                                            </Button>
                                                    }
                                                </div>
                                                {
                                                    addLabelDropDown &&
                                                        <div className={className + '__inner__body__left__labels__title semi-bold-text flex-row-center-space-between width-wfa'}>
                                                            <Autocomplete
                                                                options={labelRegistryList}
                                                                value={null}
                                                                open={labelListOpen}
                                                                onOpen={() => {
                                                                    setLabelListOpen(true);
                                                                }}
                                                                onClose={() => {
                                                                    setLabelListOpen(false);
                                                                }}
                                                                className={className + '__inner__body__left__labels__select'}
                                                                disabled={!currentUserProjectPermissions?.allowCreate || cursor === 'wait'}
                                                                getOptionLabel={(option) => option?.name}
                                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                                loading={labelListLoading}
                                                                onChange={handleLabelSelect}
                                                                renderInput={(params) => <TextField {...params}
                                                                    placeholder='Start Typing ...'
                                                                    variant='outlined'
                                                                    onChange={handleLabelTextChange}
                                                                    InputProps={{
                                                                        ...params.InputProps,
                                                                        endAdornment: (
                                                                            <React.Fragment>
                                                                                {
                                                                                    labelListLoading &&
                                                                                        <CircularProgress color='inherit' size={15} style={{marginRight: '2vw', color: 'grey'}}/>
                                                                                }
                                                                                {params.InputProps.endAdornment}
                                                                            </React.Fragment>
                                                                        ),
                                                                    }}/>}/>
                                                        </div>
                                                }
                                                <div className={className + '__inner__body__left__labels__list flex-row-center-0p5rem-gap width-wfa'}>
                                                    {
                                                        labelRegistryLinkList.map((label) =>
                                                            <LabelClickUpMenu
                                                                key={label?.id}
                                                                labelLink={label}
                                                                handleDeleteLabelRegistryClick={handleDeleteLabelRegistryClick}/>)
                                                    }
                                                </div>
                                            </div>
                                        </div>

                                        {/* Custom Fields */}
                                        <div className={className + '__card ' + className + '__card--scroll'}>
                                            {
                                                fieldList?.map((field, index) =>
                                                    <>
                                                        {
                                                            field?.type !== 'information' &&
                                                            <FieldView
                                                                field={field}
                                                                index={index}
                                                                fieldEntries={fieldEntries}
                                                                fieldList={fieldList}
                                                                ticket={ticket}/>
                                                        }
                                                    </>
                                                )
                                            }
                                        </div>
                                    </div>

                                    {/* Comments / Right column */}
                                    <div className={className + '__inner__body__right flex-row-center ticketView__right height-wfa'}>
                                        <CommentsTabs
                                            ticket={ticket}
                                            priorityList={priorityList}
                                            adminFieldList={adminFieldList}/>
                                    </div>
                                </div>
                            </>
                    }
                </div>
                <Snackbar
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                    }}
                    open={snackBarOpen}
                    autoHideDuration={5000}
                    onClose={handleSnackBarClose}>
                    <Alert
                        onClose={handleSnackBarClose}
                        severity={snackBarType}
                        variant='filled'
                        sx={{ width: '100%' }}>
                        {snackBarMessage}
                    </Alert>
                </Snackbar>
            </div>
            {
                editReportersDetails &&
                    <UpdateReportersForm
                        open={editReportersDetails}
                        closePopup={toggleEditReportersDetails}
                        renderData={renderTicketData}
                        ticket={ticket}/>
            }
            {
                showSilentCloseConfirm &&
                    <SilentCloseConfirmForm
                        open={showSilentCloseConfirm}
                        staleDays={getStaleDays()}
                        loading={cursor === 'wait'}
                        closePopup={closeSilentCloseConfirm}
                        onCloseNormally={handleCloseNormally}
                        onSilentClose={handleSilentClose}/>
            }
        </Drawer>
    );
}
